import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

/* Справка для владельца: простые пошаговые инструкции без жаргона.
   Видна только владельцу (ownerOnly в nav.js). */

const SECTIONS = [
  {
    id: 'login',
    title: 'Вход в админку',
    steps: [
      'Откройте бота (Telegram или Max) и нажмите /start — если раньше не открывали.',
      'На странице ввода введите свой номер телефона и нажмите «Продолжить».',
      'Выберите, куда прислать код — Telegram или Max.',
      'Введите 6 цифр из сообщения бота.',
      'Код не приходит: подождите минуту, проверьте, что открыли нужного бота, и нажмите «Отправить код ещё раз».',
    ],
  },
  {
    id: 'managers',
    title: 'Как добавить или убрать менеджера',
    steps: [
      'Сначала попросите человека открыть бота и нажать /start — без этого доступ не выдать.',
      'Откройте бота (Telegram или Max) и отправьте команду: /grant 89991234567 — вместо номера впишите его телефон (8 и 10 цифр без пробелов).',
      'Бот ответит, что доступ выдан. Человек увидит кнопку «Админ» в меню магазина.',
      'Убрать доступ: /revoke 89991234567 — команда та же, вместо grant — revoke.',
      'Проверить, кто менеджер: раздел «Клиенты» → найдите человека → в его карточке будет бирка «менеджер».',
      'Важно: выдавать и отзывать доступ может только владелец.',
    ],
  },
  {
    id: 'products',
    title: 'Товары: добавление, цена, фото',
    steps: [
      'Раздел «Товары» → кнопка «+ Добавить».',
      'Обязательные поля: название, категория и цена — без них не сохранится.',
      'Фото: нажмите «Выбрать фото». Важно: без фото товар НЕ попадает в блок «Что обновилось» на главной сайта.',
      'Скидка: укажите процент — старая цена зачёркнется автоматически.',
      'Остаток: сколько штук на складе. Если нужны размеры (например, 5 л, 10 л) — заполните блок «Размеры»: у каждого размера своя цена и остаток.',
      'Удалить товар: в списке нажмите корзину напротив него и подтвердите.',
    ],
  },
  {
    id: 'categories',
    title: 'Категории',
    steps: [
      'Раздел «Категории» (в меню сразу под «Товары»).',
      '«+ Добавить» — создать категорию; карандаш — переименовать или поменять фото; корзину — удалить (товары останутся, но станут без категории — лучше сначала перевести товары в другую категорию).',
      'Фото категории показывается на главной сайта в блоке категорий.',
    ],
  },
  {
    id: 'orders',
    title: 'Заказы и статусы',
    steps: [
      'Раздел «Заказы» — все заказы, новые сверху. Красная цифра на значке — сколько заказов в работе.',
      'Откройте заказ и меняйте статус выпадающим списком: Новый → Оплачен → Готов → Выдан (Доставлен).',
      'Отменённый заказ уходит из списка через неделю автоматически.',
      'Телефон клиента кликабелен — можно позвонить прямо из заказа.',
    ],
  },
  {
    id: 'broadcast',
    title: 'Рассылка в Telegram и Max',
    steps: [
      'Раздел «Рассылка» → создайте рассылку, напишите текст, при желании добавьте фото.',
      'Отправьте — пост уйдёт в оба канала сразу: Telegram и Max.',
      'Прогресс виден прямо в разделе; отправленные рассылки остаются в истории.',
    ],
  },
  {
    id: 'site',
    title: 'Сайт: фото питомника и заметки',
    steps: [
      '«Фото питомника» — фотографии в слайдере на главной сайта: загружайте, меняйте порядок, удаляйте ненужные.',
      '«Заметки» — статьи на сайте. «+ Добавить» → заголовок, текст, фото.',
      '«Срок жизни» — когда заметка скроется на сайте: например, 7 дней (полезно для сезонных советов) или «Бессрочно».',
      'Кнопка «Опубликовать» отправит заметку постом в каналы Telegram и Max — на самой заметке появятся ссылки на посты.',
      'Истёкшая заметка исчезает с сайта, но ещё 30 дней остаётся в админке (видна бирка «Скрыта на сайте»).',
    ],
  },
  {
    id: 'stats',
    title: 'Статистика и подписка',
    steps: [
      '«Статистика» — выручка и заказы за сегодня / неделю / месяц.',
      '«Подписка» — срок действия тарифа и оплата. Когда подписка закончится, админка попросит продлить.',
      '«Журнал изменений» — кто и что менял (товары, заказы, настройки).',
    ],
  },
  {
    id: 'troubles',
    title: 'Если что-то не работает',
    steps: [
      '«В доступе отказано» — под этим номером нет прав администратора; попросите владельца выдать доступ (раздел «Менеджеры»).',
      'Страница пустая или кривая — обновите её: F5 на компьютере, потяните вниз в телефоне.',
      'Что-то изменилось не так — загляните в «Журнал изменений»: там видно, кто нажимал.',
      'Не нашли ответ — напишите разработчику.',
    ],
  },
];

export default function FaqPage() {
  const [openId, setOpenId] = useState(SECTIONS[0].id);

  return (
    <div className="space-y-3 animate-fade-in">
      <div className="flex items-start gap-3 rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#6B9B74]/15 text-[#6B9B74]">
          <HelpCircle className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-[#0f172a]">Справка по админке</h2>
          <p className="mt-0.5 text-xs leading-relaxed text-[#64748b]">
            Короткие инструкции: вход, менеджеры, товары, заказы и всё остальное.
            Нажмите на раздел, чтобы развернуть.
          </p>
        </div>
      </div>

      {SECTIONS.map((section) => {
        const isOpen = openId === section.id;
        return (
          <div key={section.id} className="overflow-hidden rounded-2xl bg-white ring-1 ring-[#e2e8f0]">
            <button
              type="button"
              onClick={() => setOpenId(isOpen ? null : section.id)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-[#f8fafc]"
            >
              <span className="text-sm font-semibold text-[#0f172a]">{section.title}</span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-[#64748b] transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen && (
              <div className="border-t border-[#f1f5f9] px-4 py-3.5">
                <ol className="space-y-2.5">
                  {section.steps.map((step, i) => (
                    <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-[#374151]">
                      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#f1f5f9] text-[11px] font-bold text-[#64748b]">
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
